import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPin, Navigation, ExternalLink } from 'lucide-react';

const LocationMap = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const GOOGLE_MAPS_URL = "https://www.google.com/maps/place/Century+-+Longevity+%26+Aesthetics/@40.722696,-73.8571024,854m/data=!3m2!1e3!4b1!4m6!3m5!1s0x89c25f3efd11ec71:0x59b5c67e4f9c82e!8m2!3d40.722692!4d-73.8522315!16s%2Fg%2F11msbj1tpy!5m1!1e2?entry=ttu";

  return (
    <section ref={ref} className="py-16 bg-sage-light">
      <div className="max-w-7xl mx-auto px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-5xl font-semibold text-olive-darkest mb-3">
            Visit Our Location
          </h2>
          <div className="w-20 h-0.5 bg-gold mx-auto mb-4"></div>
          <p className="text-text-dark text-base opacity-80 max-w-3xl mx-auto">
            We're conveniently located in Forest Hills, New York. Stop by or get directions below.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2"
          >
            <div className="bg-white rounded-lg overflow-hidden shadow-xl h-[600px]">
              <iframe
                title="Century Aesthetics & Longevity Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3023.2076837419744!2d-73.85522308459395!3d40.72269197933!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c25f3efd11ec71%3A0x59b5c67e4f9c82e!2sCentury%20-%20Longevity%20%26%20Aesthetics!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.div>

          {/* Location Details */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-6"
          >
            {/* Address Card */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="bg-olive-darkest p-3 rounded-lg">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-olive-darkest mb-2">
                    Our Address
                  </h3>
                  <p className="text-text-dark text-sm opacity-80 leading-relaxed">
                    70-64 Yellowstone Boulevard<br />
                    Unit D2<br />
                    Forest Hills, NY 11375
                  </p>
                </div>
              </div>
            </div>

            {/* Directions Button */}
            <motion.a
              href={GOOGLE_MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-olive-darkest text-cream py-4 rounded-lg font-medium tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              <Navigation className="w-5 h-5" />
              Get Directions
            </motion.a>

            {/* Parking Info */}
            <div className="bg-cream rounded-lg p-6">
              <h4 className="font-semibold text-olive-darkest mb-3 text-sm flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                Parking Information
              </h4>
              <p className="text-text-dark text-xs opacity-80 leading-relaxed">
                Free parking is available on-site for all patients. Enter through the main entrance and our staff will direct you to designated parking areas.
              </p>
            </div>

            {/* Accessibility */}
            <div className="bg-cream rounded-lg p-6">
              <h4 className="font-semibold text-olive-darkest mb-3 text-sm flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Accessibility
              </h4>
              <p className="text-text-dark text-xs opacity-80 leading-relaxed">
                Our facility is wheelchair accessible with an elevator and accessible restrooms available on-site.
              </p>
            </div>

            {/* View Larger Map Link */}
            <a
              href={GOOGLE_MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-olive-dark hover:text-gold text-sm font-medium inline-flex items-center gap-2 transition-colors duration-300"
            >
              <ExternalLink className="w-4 h-4" />
              View Larger Map
            </a>
          </motion.div>
        </div>

        {/* Public Transit Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-8 bg-white rounded-lg shadow-md p-6"
        >
          <h3 className="font-heading text-xl font-semibold text-olive-darkest mb-4">
            Public Transportation
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <p className="font-semibold text-olive-darkest mb-2 text-sm">Subway</p>
              <p className="text-text-dark text-xs opacity-80">
                Take the E, F, M, or R train to Forest Hills-71st Avenue station (10 min walk)
              </p>
            </div>
            <div>
              <p className="font-semibold text-olive-darkest mb-2 text-sm">Bus</p>
              <p className="text-text-dark text-xs opacity-80">
                Q23, Q60, Q64 buses stop nearby on Yellowstone Boulevard
              </p>
            </div>
            <div>
              <p className="font-semibold text-olive-darkest mb-2 text-sm">LIRR</p>
              <p className="text-text-dark text-xs opacity-80">
                Forest Hills station is approximately 1 mile away (5 min drive or 20 min walk)
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LocationMap;